import React, { useEffect, useState } from 'react';
import * as scheduleService from '../services/scheduleService';

const STATUS_LABEL = { active: '已啟用', paused: '已暫停', completed: '已完成' };

const EMPTY_DRAFT = { name: '', cron: '', prompt: '', description: '' };

/** 執行紀錄狀態（見「Mybro 排程介接規格」）對應的顯示樣式與中文標籤。 */
const RUN_STATUS_META = {
  submitting: { dotClass: 'schedule-result-pending', label: '送出中' },
  // AGUI-1173：跟 failed 用同一個對使用者看的字「失敗」——送出失敗／執行失敗是我方系統內部的
  // 分類，使用者不需要分辨這兩種，都是「這次沒有成功」。
  submit_failed: { dotClass: 'schedule-result-failed', label: '失敗' },
  // 使用者反饋：剛觸發執行時，紀錄短暫是 queued（Relay 已受理、尚未真的開始執行）狀態，
  // 顯示「排隊中」讓人誤以為卡住沒動作；跟 running 用同一個「執行中」，使用者只需要知道
  // 「還沒結束」，不需要分辨「Relay 排隊」跟「真的在執行」這種我方系統內部的階段差異。
  queued: { dotClass: 'schedule-result-pending', label: '執行中' },
  running: { dotClass: 'schedule-result-pending', label: '執行中' },
  stop_requested: { dotClass: 'schedule-result-pending', label: '停止中' },
  succeeded: { dotClass: 'schedule-result-success', label: '成功' },
  failed: { dotClass: 'schedule-result-failed', label: '失敗' },
  cancelled: { dotClass: 'schedule-result-failed', label: '已取消' },
  superseded: { dotClass: 'schedule-result-failed', label: '已被取代' },
};

function runStatusMeta(status) {
  return RUN_STATUS_META[status] || { dotClass: 'schedule-result-pending', label: status || '未知' };
}

/** 把 mgn-quartz 回傳的 ISO 時間字串格式化成畫面上的在地時間顯示；缺值一律顯示「—」。 */
function formatDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('zh-TW', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
}

/**
 * 「下次執行」/「上次執行」專用格式(AGUI-1155):比 formatDateTime 少了「上午/下午」與秒數,
 * 固定輸出 24 小時制、不含秒,例如「2026/09/22 11:25」。只用在這兩個欄位,不影響執行結果列表
 * 既有的時間顯示(那邊仍用 formatDateTime,這次 ticket 沒有要求改)。
 */
function formatFireTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const WEEKDAY_LABEL = { MON: '一', TUE: '二', WED: '三', THU: '四', FRI: '五', SAT: '六', SUN: '日' };

/** 把 12 小時制的中文時間片語(上午/下午 hh:mm)組出來,hour 為 24 小時制數字。 */
function chineseTimeOfDay(hour, minute) {
  const h = Number(hour);
  const m = Number(minute);
  const period = h < 12 ? '上午' : '下午';
  let h12 = h % 12;
  if (h12 === 0) h12 = 12;
  return `${period} ${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * 依 Cron 的日期(dom)/星期(dow)欄位組出「每天/每週X/每月X號/每月最後一天」前綴(月份欄為 * 時用)。
 * dom 或 dow 只要不是萬用字元,就代表這個欄位「有限制」——這種情況下不管認不認得出具體格式,
 * 都不能落到最後的「每天」(那等於把一個有限制的排程講成沒有限制,是錯誤資訊,不是單純沒轉換到);
 * 認不出格式就回傳 null,交給呼叫端原樣顯示原始 Cron。
 */
function cronDayPart(dom, dow) {
  const domIsWildcard = dom === '*' || dom === '?';
  const dowIsWildcard = dow === '*' || dow === '?';
  if (!domIsWildcard) {
    if (dom.toUpperCase() === 'L') return '每月最後一天';
    if (/^\d+$/.test(dom)) return `每月 ${dom} 號`;
    if (/^\d+(?:,\d+)+$/.test(dom)) return `每月 ${dom.split(',').join('、')} 號`;
    const domRangeMatch = /^(\d+)-(\d+)$/.exec(dom);
    if (domRangeMatch) return `每月 ${domRangeMatch[1]} 至 ${domRangeMatch[2]} 號`;
    return null;
  }
  if (!dowIsWildcard) {
    const upper = dow.toUpperCase();
    if (upper === 'SAT,SUN' || upper === 'SUN,SAT') return '每週末（週六、週日）';
    if (upper.includes('-')) {
      const [a, b] = upper.split('-');
      if (WEEKDAY_LABEL[a] && WEEKDAY_LABEL[b]) return `每週${WEEKDAY_LABEL[a]}至週${WEEKDAY_LABEL[b]}`;
      return null;
    }
    if (upper.includes(',')) {
      const tokens = upper.split(',');
      if (!tokens.every((token) => WEEKDAY_LABEL[token])) return null;
      return `每週${tokens.map((token) => WEEKDAY_LABEL[token]).join('、')}`;
    }
    if (WEEKDAY_LABEL[upper]) return `每週${WEEKDAY_LABEL[upper]}`;
    return null;
  }
  return '每天';
}

/**
 * 月份欄(mon)不是 * 時,「每月/每週」的語意會被「每季」「每年」取代(見 schedule-cron-advanced
 * skill 的每季/每年換算範例)——這裡沒辨識出組合方式就回傳 null,由呼叫端決定原樣顯示原始 Cron,
 * 不能落回 cronDayPart 硬套「每月」,那樣會把「每季第一天」「每年 1 月 1 號」誤標成「每月 1 號」。
 */
function monthAwareDayPart(dom, mon, dow) {
  if (mon === '*') return cronDayPart(dom, dow);
  const monthTokens = mon.split(',').map((t) => t.trim());
  const isQuarterly = monthTokens.length === 4 && ['1', '4', '7', '10'].every((m) => monthTokens.includes(m));
  if (isQuarterly) {
    if (dom.toUpperCase() === 'L') return '每季最後一天';
    if (/^\d+$/.test(dom)) return dom === '1' ? '每季第一天' : `每季 ${dom} 號`;
    return null;
  }
  if (monthTokens.length === 1 && /^\d+$/.test(monthTokens[0])) {
    const monthNum = monthTokens[0];
    if (dom.toUpperCase() === 'L') return `每年 ${monthNum} 月最後一天`;
    if (/^\d+$/.test(dom)) return `每年 ${monthNum} 月 ${dom} 號`;
    return null;
  }
  return null;
}

/**
 * 只執行一次的排程(七碼 Cron,秒 分 時 日 月 週 年——見 Cron 換算一節「一次性」規則)沒有重複語意,
 * 不套用上面的「每天/每週/每月」邏輯,直接還原成絕對日期時間顯示,例如「僅執行一次：2026/09/17
 * 下午 02:59」。
 */
function formatOneTimeCron(fields) {
  const [sec, min, hour, dom, mon, , year] = fields;
  if (![sec, min, hour, dom, mon, year].every((v) => /^\d+$/.test(v))) return fields.join(' ');
  const d = new Date(Number(year), Number(mon) - 1, Number(dom), Number(hour), Number(min), Number(sec));
  if (Number.isNaN(d.getTime())) return fields.join(' ');
  const pad = (n) => String(n).padStart(2, '0');
  const dateStr = `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())}`;
  return `僅執行一次：${dateStr} ${chineseTimeOfDay(d.getHours(), d.getMinutes())}`;
}

/**
 * 把 Quartz Cron 表達式轉成中文易讀文字(AGUI-1155)。涵蓋範圍以 schedule-tool／
 * schedule-cron-advanced 兩份 skill 文件裡實際會換算出來的全部 Cron 樣式為準(不只是 Jira
 * 單子裡舉的例子),包括:一次性(七碼,含年)、每天/每週/每月固定時間、每季、每年、每月最後一天、
 * 高頻/區間執行、每 N 分鐘或每 N 小時一次、每小時固定分鐘。無法辨識的格式原樣顯示原始 Cron,
 * ⛔ 不猜測、不硬套一個可能講錯的說法(例如把「每季第一天」誤標成「每月 1 號」)。
 */
function cronToReadableText(cronExpression) {
  if (!cronExpression || cronExpression === '—') return cronExpression || '—';
  const fields = cronExpression.trim().split(/\s+/);
  if (fields.length === 7) return formatOneTimeCron(fields);
  if (fields.length !== 6) return cronExpression;
  const [, min, hour, dom, mon, dow] = fields;

  const withDayPart = (suffix) => {
    const dayPart = monthAwareDayPart(dom, mon, dow);
    if (dayPart === null) return cronExpression;
    return dayPart === '每天' ? suffix : `${dayPart} ${suffix}`;
  };

  // 每 N 分鐘一次,全天不限時段(小時欄為 *,分鐘欄為 */N 或 0/N 的 step)
  const wildcardMinuteStep = /^(?:\*|0)\/(\d+)$/.exec(min);
  if (hour === '*' && wildcardMinuteStep) {
    return withDayPart(`每 ${wildcardMinuteStep[1]} 分鐘一次`);
  }

  // 每小時固定分鐘(小時欄為 *,分鐘欄是單一數字)
  if (hour === '*' && /^\d+$/.test(min)) {
    return withDayPart(`每小時的第 ${min} 分鐘`);
  }

  // 每 N 小時一次(小時欄為 */N,分鐘欄固定為 0)
  const hourStepMatch = /^\*\/(\d+)$/.exec(hour);
  if (hourStepMatch && min === '0') {
    return withDayPart(`每 ${hourStepMatch[1]} 小時一次`);
  }

  const hourRangeMatch = /^(\d+)-(\d+)$/.exec(hour);
  if (hourRangeMatch) {
    const startTime = chineseTimeOfDay(hourRangeMatch[1], 0);
    const endTime = chineseTimeOfDay(hourRangeMatch[2], 0);
    // 區間本身就是「限定時段」的說明,不像純粹的每小時重複那樣冗贅,「每天」要保留(不套用
    // withDayPart 省略前綴的規則),否則「上午 09:00 至 下午 06:00」讀起來像只發生一次。
    const dayPart = monthAwareDayPart(dom, mon, dow);
    if (dayPart === null) return cronExpression;
    // 區間內每 N 分鐘一次(例如上班時間每 30 分鐘)
    const minStepMatch = /^(\d+)\/(\d+)$/.exec(min);
    if (minStepMatch) {
      return `${dayPart} ${startTime} 至 ${endTime}，每 ${minStepMatch[2]} 分鐘一次`;
    }
    // 區間內每小時固定分鐘(例如上班時間每小時整點)
    if (/^\d+$/.test(min)) {
      return `${dayPart} ${startTime} 至 ${endTime}，每小時的第 ${min} 分鐘`;
    }
  }

  // 一般固定時間執行(每天/每週X/每月X號/每季/每年,依 monthAwareDayPart 判斷)
  if (/^\d+$/.test(hour) && /^\d+$/.test(min)) {
    const dayPart = monthAwareDayPart(dom, mon, dow);
    if (dayPart === null) return cronExpression;
    return `${dayPart} ${chineseTimeOfDay(hour, min)}`;
  }

  return cronExpression;
}

/**
 * mgn-quartz 的 Trigger 狀態(NORMAL/PAUSED/COMPLETE/ERROR/BLOCKED/NONE)在畫面上分三種：
 * 「已暫停」、「已完成」（一次性排程自然執行完畢、Quartz 已無對應 job，但使用者尚未刪除——
 * 這種情況 cron 來自後端存的最後一筆歷史快照，nextFireTime/previousFireTime 為 null）、
 * 其餘一律視為「已啟用」。
 */
function mapJobDetailsToSchedule(job) {
  const dataMap = job.jobDataMap || {};
  const status = job.status === 'PAUSED' ? 'paused' : job.status === 'COMPLETE' ? 'completed' : 'active';
  return {
    id: job.jobGroupName,
    name: job.displayName || job.jobGroupName,
    status,
    cron: job.cronExpression || '—',
    description: job.jobDescription || '',
    prompt: dataMap.prompt || dataMap.message || '',
    nextRun: formatFireTime(job.nextFireTime),
    lastRun: formatFireTime(job.previousFireTime),
  };
}

function ActionIcon({ type }) {
  switch (type) {
    case 'run':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polygon points="5 3 19 12 5 21 5 3" />
        </svg>
      );
    case 'pause':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="6" y="4" width="4" height="16" />
          <rect x="14" y="4" width="4" height="16" />
        </svg>
      );
    case 'resume':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polygon points="5 3 19 12 5 21 5 3" />
          <line x1="22" y1="4" x2="22" y2="20" />
        </svg>
      );
    case 'edit':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
        </svg>
      );
    case 'delete':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M3 6h18" />
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
          <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        </svg>
      );
    case 'cancel':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      );
    case 'save':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      );
    case 'refresh':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="23 4 23 10 17 10" />
          <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
        </svg>
      );
    case 'list':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      );
    case 'add':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      );
    default:
      return null;
  }
}

// AGUI-1169：mobileListOpen／setMobileListOpen 由 App.js 往下傳，不是這裡自己的 local
// state——Sidebar 的全域選單開關按鈕也需要知道排程清單抽屜是不是開著（兩個抽屜同時開會
// 疊在一起），這件事得由兩者共同的父層（App.js）管理狀態，才能同步。
const ScheduleView = ({ isMobile, mobileListOpen, setMobileListOpen }) => {
  const [schedules, setSchedules] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  // formMode: 'view' | 'edit' | 'create'
  const [formMode, setFormMode] = useState('view');
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [scheduleToDelete, setScheduleToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [isBusy, setIsBusy] = useState(false);
  const [runHistory, setRunHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const HISTORY_LIMIT = 20;
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [expandedRunId, setExpandedRunId] = useState(null);
  const [expandedRunDetails, setExpandedRunDetails] = useState({});
  const [toast, setToast] = useState(null);

  const selected = schedules.find((s) => s.id === selectedId) || null;

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), toast.duration || 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const fetchSchedules = async () => {
    try {
      setLoadError(null);
      const result = await scheduleService.listSchedules({ page: 0, size: 100 });
      const content = result?.content || [];
      const mapped = content.map(mapJobDetailsToSchedule);
      setSchedules(mapped);
      setSelectedId((prevId) => {
        if (mapped.some((s) => s.id === prevId)) return prevId;
        return mapped[0]?.id ?? null;
      });
    } catch (error) {
      setLoadError(error.message || '排程列表載入失敗，請稍後再試。');
    }
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await fetchSchedules();
      setIsLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setExpandedRunId(null);
    setExpandedRunDetails({});
    if (!selected || formMode !== 'view') {
      setRunHistory([]);
      setHistoryTotal(0);
      setHistoryError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      // 重新整理排程列表,讓下次/上次觸發時間等即時 Quartz 狀態跟著更新——這兩個欄位只在
      // fetchSchedules() 被呼叫時才會取得新值,單純重新選取/開啟同一筆排程並不會自動刷新,
      // 否則畫面會停留在上次讀取當下的快照,顯示比後續實際執行紀錄還舊的時間。
      await fetchSchedules();
      setHistoryLoading(true);
      setHistoryError(null);
      try {
        const result = await scheduleService.getScheduleHistory(selected.id, { limit: HISTORY_LIMIT });
        if (!cancelled) {
          setRunHistory(result?.runs || []);
          setHistoryTotal(result?.total ?? 0);
        }
      } catch (error) {
        if (!cancelled) setHistoryError(error.message || '執行歷史載入失敗，請稍後再試。');
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id, formMode]);

  const loadRunDetail = async (jobGroupName, runId) => {
    setExpandedRunDetails((prev) => ({ ...prev, [runId]: { loading: true, error: null, detail: null } }));
    try {
      const detail = await scheduleService.getScheduleRun(jobGroupName, runId);
      setExpandedRunDetails((prev) => ({ ...prev, [runId]: { loading: false, error: null, detail } }));
    } catch (error) {
      setExpandedRunDetails((prev) => ({
        ...prev,
        [runId]: { loading: false, error: error.message || '執行結果載入失敗，請稍後再試。', detail: null },
      }));
    }
  };

  const handleToggleRunExpand = (run) => {
    const runId = run.run_id;
    if (expandedRunId === runId) {
      setExpandedRunId(null);
      return;
    }
    setExpandedRunId(runId);
    if (!selected) return;
    // 快取的 detail 只有在「狀態跟列表上這筆一致」時才沿用：展開當下還在執行中，之後列表
    // 已經變成成功／失敗的話，快取的就是過期快照（會一直顯示「執行中，尚無結果」），要重抓。
    // 上次載入失敗的也重抓，不要讓一次暫時性錯誤卡住這筆。
    const cached = expandedRunDetails[runId];
    if (cached && (cached.loading || (!cached.error && cached.detail?.status === run.status))) return;
    loadRunDetail(selected.id, runId);
  };

  const handleSelect = (schedule) => {
    setSelectedId(schedule.id);
    setFormMode('view');
    setMobileListOpen(false);
    // 排程清單只有在掛載、按「重新整理」、或異動排程後才會重抓，切換選取的排程時「下次」/「上次」
    // 這種隨時間變化的欄位可能早就過期了（尤其是每分鐘觸發這種高頻排程）；這裡不等待、不擋
    // UI，背景重抓一次順便更新這兩個欄位。
    fetchSchedules();
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchSchedules();
    // AGUI-1169：重新整理排程清單時，右側目前選取排程的執行結果也要一併重新整理，
    // 不然清單刷新了，右邊卻還停在舊的執行結果快照上。
    if (selected && formMode === 'view') {
      setHistoryLoading(true);
      setHistoryError(null);
      try {
        const result = await scheduleService.getScheduleHistory(selected.id, { limit: HISTORY_LIMIT });
        const runs = result?.runs || [];
        setRunHistory(runs);
        setHistoryTotal(result?.total ?? 0);
        // 列表刷新了，已抓過的單筆結果也一併作廢，否則展開後看到的仍是舊快照；
        // 目前展開中的那筆直接重抓，讓畫面立即反映最新結果。
        setExpandedRunDetails({});
        if (expandedRunId && runs.some((r) => r.run_id === expandedRunId)) {
          loadRunDetail(selected.id, expandedRunId);
        }
      } catch (error) {
        setHistoryError(error.message || '執行歷史載入失敗，請稍後再試。');
      } finally {
        setHistoryLoading(false);
      }
    }
    setIsRefreshing(false);
  };

  const handleRunNow = async () => {
    if (!selected || isBusy) return;
    setIsBusy(true);
    try {
      await scheduleService.runScheduleNow(selected.id);
      // AGUI-1166：Jira 票上明訂文字與時長——「排程任務已觸發」，顯示約 2 秒後消失
      // （比照 MyAgent 排程頁的簡短提示，不是原本這句解釋完整背景的長文案）。
      setToast({ type: 'pending', text: '排程任務已觸發', duration: 2000 });
    } catch (error) {
      setToast({ type: 'error', text: error.message || '立即執行失敗，請稍後再試。' });
    } finally {
      setIsBusy(false);
    }
  };

  const handlePause = async () => {
    if (!selected || isBusy) return;
    setIsBusy(true);
    try {
      await scheduleService.pauseSchedule(selected.id);
      await fetchSchedules();
    } catch (error) {
      alert(error.message || '暫停排程失敗，請稍後再試。');
    } finally {
      setIsBusy(false);
    }
  };

  const handleResume = async () => {
    if (!selected || isBusy) return;
    setIsBusy(true);
    try {
      await scheduleService.resumeSchedule(selected.id);
      await fetchSchedules();
    } catch (error) {
      alert(error.message || '恢復排程失敗，請稍後再試。');
    } finally {
      setIsBusy(false);
    }
  };

  const handleStartCreate = () => {
    setDraft(EMPTY_DRAFT);
    setFormMode('create');
    setMobileListOpen(false);
  };

  const handleStartEdit = () => {
    if (!selected) return;
    setDraft({
      name: selected.name,
      cron: selected.cron,
      prompt: selected.prompt,
      description: selected.description,
    });
    setFormMode('edit');
  };

  const handleCancelForm = () => {
    setFormMode('view');
  };

  const handleSaveForm = async () => {
    const displayName = draft.name.trim();
    const cronExpression = draft.cron.trim();
    const prompt = draft.prompt.trim();

    if (!displayName) {
      alert('請輸入顯示名稱。');
      return;
    }
    if (!cronExpression) {
      alert('請輸入排程時間（Quartz 六碼 Cron 表達式）。');
      return;
    }
    if (!prompt) {
      alert('請輸入提示詞，否則排程時間到也不會真的送出任何內容給 AI。');
      return;
    }

    setIsBusy(true);
    try {
      let nextSelectedId = selected?.id ?? null;
      if (formMode === 'create') {
        // jobGroupName（內部識別碼）由後端產生，回應裡的 jobGroupName 才是這筆排程之後
        // 編輯/暫停/刪除/查歷史要用的 id。
        const created = await scheduleService.createSchedule({
          displayName,
          cronExpression,
          jobDescription: draft.description.trim() || undefined,
          prompt,
        });
        nextSelectedId = created?.jobGroupName ?? null;
      } else if (formMode === 'edit' && selected) {
        await scheduleService.updateSchedule(selected.id, {
          cronExpression,
          jobDescription: draft.description.trim() || undefined,
          prompt,
          displayName,
        });
      }
      await fetchSchedules();
      setSelectedId(nextSelectedId);
      setFormMode('view');
    } catch (error) {
      alert(error.message || '儲存排程失敗，請稍後再試。');
    } finally {
      setIsBusy(false);
    }
  };

  const handleAskDelete = () => {
    if (selected) setScheduleToDelete(selected);
  };

  const cancelDeleteSchedule = () => setScheduleToDelete(null);

  const confirmDeleteSchedule = async () => {
    if (!scheduleToDelete) return;
    setIsBusy(true);
    try {
      await scheduleService.deleteSchedule(scheduleToDelete.id);
      await fetchSchedules();
    } catch (error) {
      alert(error.message || '刪除排程失敗，請稍後再試。');
    } finally {
      setIsBusy(false);
      setScheduleToDelete(null);
      setFormMode('view');
    }
  };

  const isFormMode = formMode === 'edit' || formMode === 'create';

  return (
    <div className={`schedule-view ${isMobile ? 'schedule-view--mobile' : ''}`}>
      {toast && (
        <div className={`schedule-toast schedule-toast--${toast.type}`} role="status" onClick={() => setToast(null)}>
          {toast.type === 'pending' && <span className="schedule-toast-spinner" aria-hidden="true" />}
          {toast.text}
        </div>
      )}


      {scheduleToDelete && (
        <div className="delete-confirm-overlay" onClick={cancelDeleteSchedule}>
          <div className="delete-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="delete-confirm-title">刪除排程「{scheduleToDelete.name}」？此操作無法復原。</div>
            <div className="delete-confirm-buttons">
              <button className="delete-confirm-btn cancel-btn" onClick={cancelDeleteSchedule}>
                取消
              </button>
              <button className="delete-confirm-btn confirm-btn" onClick={confirmDeleteSchedule} disabled={isBusy}>
                刪除
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="schedule-view-body">
        {isMobile && mobileListOpen && (
          <div className="sidebar-backdrop" onClick={() => setMobileListOpen(false)} aria-hidden="true" />
        )}

        <aside className={`schedule-list ${isMobile ? 'mobile-drawer' : ''} ${isMobile && mobileListOpen ? 'mobile-open' : ''}`}>
          <div className="schedule-list-header">
            <span>排程工作</span>
            <div className="schedule-list-header-actions">
              <button
                type="button"
                className={`schedule-list-refresh ${isRefreshing ? 'spinning' : ''}`}
                title="重新整理"
                aria-label="重新整理"
                onClick={handleRefresh}
                disabled={isRefreshing}
              >
                <ActionIcon type="refresh" />
              </button>
              {/* AGUI-1169：點擊不再跳提示，反灰按鈕純粹是視覺上的「尚未開放」標示，
                  點了沒有任何反應——新增排程請直接在對話框告訴 MyBro。 */}
              <button
                type="button"
                className="schedule-list-add schedule-list-add--disabled"
                title="此功能即將推出，你可以先試試在對話框新增排程"
                aria-label="新增排程（此功能尚未開放，請改用對話框）"
                aria-disabled="true"
              >
                <ActionIcon type="add" />
              </button>
              {isMobile && (
                <button
                  type="button"
                  className="schedule-list-mobile-close"
                  title="關閉排程工作清單"
                  aria-label="關閉排程工作清單"
                  onClick={() => setMobileListOpen(false)}
                >
                  <ActionIcon type="cancel" />
                </button>
              )}
            </div>
          </div>
          <div className="schedule-list-items">
            {isLoading ? (
              <div className="schedule-results-empty">載入中…</div>
            ) : loadError ? (
              <div className="schedule-results-empty">{loadError}</div>
            ) : schedules.length === 0 ? (
              <div className="schedule-results-empty">目前沒有任何排程，請直接在對話框告訴我要建立什麼排程。</div>
            ) : (
              schedules.map((s) => (
                <div
                  key={s.id}
                  className={`schedule-list-item ${formMode !== 'create' && selectedId === s.id ? 'active' : ''}`}
                  onClick={() => handleSelect(s)}
                >
                  <span className="schedule-item-agent-badge" aria-hidden="true">🤖</span>
                  <span className="schedule-item-name" title={s.name}>{s.name}</span>
                  <span className={`schedule-status-pill schedule-status-${s.status}`}>
                    {STATUS_LABEL[s.status]}
                  </span>
                </div>
              ))
            )}
          </div>
        </aside>

        <div className={`schedule-main ${isMobile && mobileListOpen ? 'schedule-main--hidden' : ''}`}>
          <div className={`schedule-view-header ${isMobile ? 'schedule-view-header--mobile' : ''}`}>
            {isMobile && (
              <button
                type="button"
                className="schedule-icon-btn schedule-list-mobile-toggle"
                title="排程工作清單"
                aria-label="開啟排程工作清單"
                onClick={() => setMobileListOpen(true)}
              >
                <ActionIcon type="list" />
              </button>
            )}
            {(!selected || isFormMode) && (
              <div className="schedule-view-title" title={selected?.name}>
                {formMode === 'create'
                  ? '新任務'
                  : formMode === 'edit'
                    ? `編輯．${selected?.name ?? ''}`
                    : '選擇排程任務'}
              </div>
            )}
            <div className="schedule-view-actions">
              {isFormMode ? (
                <>
                  <div className="tooltip-wrapper">
                    <button type="button" className="schedule-icon-btn" onClick={handleCancelForm}>
                      <ActionIcon type="cancel" />
                    </button>
                    <span className="tooltip">取消</span>
                  </div>
                  <div className="tooltip-wrapper">
                    <button type="button" className="schedule-icon-btn schedule-icon-btn--primary" onClick={handleSaveForm} disabled={isBusy}>
                      <ActionIcon type="save" />
                    </button>
                    <span className="tooltip">儲存</span>
                  </div>
                </>
              ) : selected ? (
                <>
                  {selected.status !== 'completed' && (
                    <div className="tooltip-wrapper">
                      <button type="button" className="schedule-icon-btn" onClick={handleRunNow} disabled={isBusy}>
                        <ActionIcon type="run" />
                      </button>
                      <span className="tooltip">立即執行</span>
                    </div>
                  )}
                  {selected.status !== 'completed' && (
                    selected.status === 'paused' ? (
                      <div className="tooltip-wrapper">
                        <button type="button" className="schedule-icon-btn" onClick={handleResume} disabled={isBusy}>
                          <ActionIcon type="resume" />
                        </button>
                        <span className="tooltip">啟用排程</span>
                      </div>
                    ) : (
                      <div className="tooltip-wrapper">
                        <button type="button" className="schedule-icon-btn" onClick={handlePause} disabled={isBusy}>
                          <ActionIcon type="pause" />
                        </button>
                        <span className="tooltip">暫停排程</span>
                      </div>
                    )
                  )}
                  {/* AGUI-1169：點擊不再跳提示，反灰按鈕純粹是視覺上的「尚未開放」標示，
                      點了沒有任何反應——修改排程請直接在對話框告訴 MyBro。
                      AGUI-1163：不用 tooltip-wrapper/span 自訂 tooltip，只留原生 title 屬性，
                      避免兩者疊加造成 hover 時出現雙重提示。 */}
                  <button
                    type="button"
                    className="schedule-icon-btn schedule-icon-btn--disabled"
                    title="此功能即將推出，你可以先試試在對話框修改排程"
                    aria-label="編輯排程（此功能尚未開放，請改用對話框）"
                    aria-disabled="true"
                  >
                    <ActionIcon type="edit" />
                  </button>
                  <div className="tooltip-wrapper">
                    <button type="button" className="schedule-icon-btn schedule-icon-btn--danger" onClick={handleAskDelete} disabled={isBusy}>
                      <ActionIcon type="delete" />
                    </button>
                    <span className="tooltip">刪除排程</span>
                  </div>
                </>
              ) : null}
            </div>
          </div>

          {isFormMode ? (
            <div className="schedule-form">
              <div className="schedule-form-row">
                <label htmlFor="scheduleFormName">顯示名稱</label>
                <input
                  type="text"
                  id="scheduleFormName"
                  placeholder="必填，之後可以隨時修改"
                  autoComplete="off"
                  value={draft.name}
                  onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                />
                <div className="schedule-form-hint">單純用來辨識這個排程，可以跟其他排程同名，之後也能隨時修改。</div>
              </div>
              <div className="schedule-form-row">
                <label htmlFor="scheduleFormCron">排程</label>
                <input
                  type="text"
                  id="scheduleFormCron"
                  placeholder="0 0 12 * * ?"
                  autoComplete="off"
                  value={draft.cron}
                  onChange={(e) => setDraft((d) => ({ ...d, cron: e.target.value }))}
                />
                <div className="schedule-form-hint">
                  Quartz 六碼 Cron 表達式（秒 分 時 日 月 星期），例如每天中午 12 點是「0 0 12 * * ?」。
                </div>
              </div>
              <div className="schedule-form-row schedule-form-row--grow">
                <label htmlFor="scheduleFormPrompt">提示</label>
                <textarea
                  id="scheduleFormPrompt"
                  placeholder="時間到要送給 AI 的提示詞"
                  value={draft.prompt}
                  onChange={(e) => setDraft((d) => ({ ...d, prompt: e.target.value }))}
                />
              </div>
              <div className="schedule-form-row">
                <label htmlFor="scheduleFormDescription">說明（選填）</label>
                <input
                  type="text"
                  id="scheduleFormDescription"
                  placeholder="這個排程的用途備註"
                  autoComplete="off"
                  value={draft.description}
                  onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                />
              </div>
            </div>
          ) : selected ? (
            <>
              <div className="schedule-meta-card">
                <div className="schedule-meta-row">
                  <div className="schedule-meta-label">名稱</div>
                  <div className="schedule-meta-value">{selected.name}</div>
                </div>
                <div className="schedule-meta-row">
                  <div className="schedule-meta-label">狀態</div>
                  <div className="schedule-meta-value">
                    <span className={`schedule-status-pill schedule-status-${selected.status}`}>
                      {STATUS_LABEL[selected.status]}
                    </span>
                  </div>
                </div>
                <div className="schedule-meta-row">
                  <div className="schedule-meta-label">執行頻率</div>
                  <div className="schedule-meta-value" title={selected.cron}>{cronToReadableText(selected.cron)}</div>
                </div>
                <div className="schedule-meta-row">
                  <div className="schedule-meta-label">下次執行</div>
                  <div className="schedule-meta-value">{selected.nextRun}</div>
                </div>
                <div className="schedule-meta-row">
                  <div className="schedule-meta-label">上次執行</div>
                  <div className="schedule-meta-value">{selected.lastRun}</div>
                </div>
                {selected.description && (
                  <div className="schedule-meta-row">
                    <div className="schedule-meta-label">工作說明</div>
                    <div className="schedule-meta-value">{selected.description}</div>
                  </div>
                )}
              </div>

              <div className="schedule-prompt-wrap">
                <div className="schedule-prompt-label">執行內容</div>
                <div className="schedule-prompt-display">{selected.prompt}</div>
              </div>

              <div className="schedule-results">
                <div className="schedule-results-header">
                  執行結果
                  {!historyLoading && !historyError && runHistory.length > 0 && (
                    <span className="schedule-results-header-note">
                      僅顯示最近 {HISTORY_LIMIT} 筆{historyTotal > HISTORY_LIMIT ? `（共 ${historyTotal} 筆）` : ''}
                    </span>
                  )}
                </div>
                <div className="schedule-results-list">
                  {historyLoading ? (
                    <div className="schedule-results-empty">載入中…</div>
                  ) : historyError ? (
                    <div className="schedule-results-empty">{historyError}</div>
                  ) : runHistory.length === 0 ? (
                    <div className="schedule-results-empty">尚無執行紀錄。</div>
                  ) : (
                    runHistory.map((run) => {
                      const meta = runStatusMeta(run.status);
                      const isOpen = expandedRunId === run.run_id;
                      const detailState = expandedRunDetails[run.run_id];
                      return (
                        <div className="schedule-result-item" key={run.run_id}>
                          <div
                            className="schedule-result-head"
                            onClick={() => handleToggleRunExpand(run)}
                            role="button"
                            tabIndex={0}
                          >
                            <div className="schedule-result-time-wrap">
                              <span className={`schedule-result-dot ${meta.dotClass}`} />
                              <span className="schedule-result-time">{formatDateTime(run.created_at)}</span>
                            </div>
                            <span className="schedule-result-size">{meta.label}</span>
                            <span className={`schedule-result-expand ${isOpen ? 'open' : ''}`}>▾</span>
                          </div>
                          {isOpen && (
                            <div className="schedule-result-body">
                              {detailState?.loading
                                ? '載入中…'
                                : detailState?.error
                                ? detailState.error
                                : detailState?.detail?.result?.text
                                ? detailState.detail.result.text
                                // AGUI-1173：submit_failed／failed 這兩種真正的失敗狀態，detail.message
                                // 存的是我方或 Relay 呼叫失敗的原始技術訊息（例如「Relay 呼叫失敗：
                                // HTTP 404, body={"detail":"Not Found"}」），對使用者來說是無意義的
                                // 錯誤代碼，一律改講固定的白話文字；cancelled／superseded 不算「系統
                                // 錯誤」（分別是使用者取消、被更新的執行取代），不套用這個 fallback，
                                // 維持顯示原本的 message（如果有的話）。
                                : run.status === 'submit_failed' || run.status === 'failed'
                                ? '排程執行失敗：系統錯誤。'
                                : detailState?.detail?.message
                                ? detailState.detail.message
                                // 已成功但沒拿到內容時不能再說「執行中」，會跟列表上的「成功」矛盾。
                                : run.status === 'succeeded'
                                ? '執行完成，但沒有回傳內容。'
                                : '執行中，尚無結果。'}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="schedule-empty-state">
              <div className="schedule-empty-state-title">尚無排程</div>
              <div className="schedule-empty-state-sub">請直接在對話框告訴我要建立什麼排程。</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScheduleView;
